import React from "react";
import { Manager, Reference, Popper, PopperProps } from "react-popper";
import { Omit } from "typings";
import { StyledTheme } from "../../theme";
import { useToggle } from "../../hooks";
import Box from "../Box/Box";
import Text from "../Text/Text";

const popperStyles = {
  bottom: { mt: 4 },
  top: { mb: 4 },
  right: { ml: 4 },
  left: { mr: 4 },
};

const arrowStyles = {
  bottom: {
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    marginTop: "-0.5rem",
    "&::before": {
      transform: "rotate(45deg)",
    },
  },
  top: {
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    marginBottom: "-0.4rem",
    "&::before": {
      transform: "rotate(-135deg)",
    },
  },
  right: {
    left: 0,
    top: "50%",
    transform: "translateY(-50%)",
    marginLeft: "-0.5rem",
    "&::before": {
      transform: "rotate(-45deg)",
    },
  },
  left: {
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    marginRight: "-0.4rem",
    "&::before": {
      transform: "rotate(135deg)",
    },
  },
};

interface Props extends Omit<PopperProps, "children"> {
  refProps?: any;
  content: React.ReactNode;
  placement?: keyof typeof popperStyles;
  interaction?: "hover" | "click";
}

const Tooltip: React.FunctionComponent<Props> = ({
  children,
  refProps,
  content,
  placement: initialPlacement = "top",
  interaction = "hover",
  ...restProps
}) => {
  const { isOpen, onOpen, onClose, onToggle } = useToggle();

  const referenceProps = {
    onMouseEnter: interaction === "hover" ? onOpen : undefined,
    onMouseLeave: interaction === "hover" ? onClose : undefined,
    onClick: interaction === "click" ? onToggle : undefined,
    ...refProps,
  };

  return (
    <Manager>
      <Reference>
        {({ ref }) =>
          typeof children === "string" ? (
            <Text tabIndex={0} {...referenceProps} ref={ref}>
              {children}
            </Text>
          ) : (
            React.cloneElement(React.Children.only(children as any), {
              ...referenceProps,
              ref,
            })
          )
        }
      </Reference>
      {isOpen && (
        <Popper placement={initialPlacement} {...restProps}>
          {({ ref, style, placement, arrowProps }) => (
            <Box
              ref={ref}
              style={style}
              data-placement={placement}
              sx={
                {
                  maxWidth: "18.75rem",
                  px: 4,
                  py: 3,
                  bg: "background",
                  borderRadius: "default",
                  border: (theme: StyledTheme) =>
                    `1px solid ${theme.colors.border}`,
                  ...popperStyles[placement as "top"],
                } as any
              }
            >
              {content}
              {placement && (
                <Box
                  ref={arrowProps.ref}
                  style={arrowProps.style}
                  sx={{
                    position: "absolute",
                    width: "0.8rem",
                    height: "0.8rem",
                    ...arrowStyles[placement as "top"],

                    "&::before": {
                      content: "''",
                      margin: "auto",
                      display: "block",
                      width: "0.8rem",
                      height: "0.8rem",
                      borderStyle: "solid",
                      borderWidth: "1px",
                      borderColor: (theme: StyledTheme) =>
                        `${theme.colors.border} transparent transparent ${theme.colors.border}`,
                      bg: "background",
                      ...arrowStyles[placement as "top"]["&::before"],
                    },
                  }}
                />
              )}
            </Box>
          )}
        </Popper>
      )}
    </Manager>
  );
};

export default Tooltip;
